import React, { useEffect, useState } from 'react';
import { Alert, Button, Chip, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { formatMoney } from '../../components/commercial/contact';
import { todayIso } from '../../components/expenses/ExpenseDialog';
import SupplierPicker, { PickedSupplier } from '../../components/expenses/SupplierPicker';

/** Cargar un gasto del día a día (o una deuda con un proveedor) desde el celular. */
const MobileExpensePage: React.FC = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<string[]>([]);
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [payable, setPayable] = useState(false);
  const [supplier, setSupplier] = useState<PickedSupplier | null>(null);
  const [dueDate, setDueDate] = useState(todayIso());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<number | null>(null);

  useEffect(() => { api.getExpenseCategories().then(setCategories).catch(() => setCategories(['General'])); }, []);

  const amountNum = Number(amount.replace(',', '.')) || 0;

  const submit = async () => {
    if (amountNum <= 0) { setError('Poné el monto'); return; }
    if (!category) { setError('Elegí la categoría'); return; }
    if (payable && !supplier) { setError('Elegí el proveedor'); return; }
    setSaving(true);
    setError(null);
    try {
      await api.createOneTimeExpense({
        description: description.trim() || category,
        amount: amountNum,
        date: todayIso(),
        category,
        supplierId: supplier?.id ?? null,
        isPayable: payable,
        dueDate: payable ? dueDate : null,
      });
      setDone(amountNum);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo guardar el gasto');
    } finally {
      setSaving(false);
    }
  };

  if (done != null) {
    return (
      <Stack spacing={2} alignItems="center" sx={{ pt: 4, textAlign: 'center' }}>
        <CheckCircle color="success" sx={{ fontSize: 80 }} />
        <Typography variant="h5" fontWeight={800}>{payable ? 'Deuda cargada' : 'Gasto cargado'}</Typography>
        <Typography variant="h4" fontWeight={800}>{formatMoney(done)}</Typography>
        <Typography color="text.secondary">{category}{description ? ` · ${description}` : ''}</Typography>
        <Button variant="contained" size="large" sx={{ minHeight: 56, width: '100%' }}
          onClick={() => { setDone(null); setAmount(''); setDescription(''); setSupplier(null); setPayable(false); }}>Otro gasto</Button>
        <Button variant="outlined" size="large" sx={{ minHeight: 56, width: '100%' }} onClick={() => navigate('/m')}>Volver al inicio</Button>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <TextField
        label="Monto"
        value={amount}
        onChange={e => setAmount(e.target.value)}
        autoFocus
        inputProps={{ inputMode: 'decimal', style: { fontSize: 32, fontWeight: 800, textAlign: 'center' } }}
      />
      <Typography variant="body2" color="text.secondary">Categoría</Typography>
      <Stack direction="row" flexWrap="wrap" gap={1}>
        {categories.map(c => (
          <Chip key={c} label={c} onClick={() => setCategory(c)} color={category === c ? 'primary' : 'default'}
            variant={category === c ? 'filled' : 'outlined'} sx={{ fontSize: 15, height: 40 }} />
        ))}
      </Stack>
      <TextField label="Detalle (opcional)" value={description} onChange={e => setDescription(e.target.value)} placeholder="Ej: nafta reparto zona norte"
        InputProps={{ sx: { fontSize: 18, minHeight: 56 } }} />
      <ToggleButtonGroup exclusive fullWidth value={payable ? 'debt' : 'paid'} onChange={(_, v) => v && setPayable(v === 'debt')}>
        <ToggleButton value="paid" sx={{ minHeight: 52, fontSize: 16 }}>Ya lo pagué</ToggleButton>
        <ToggleButton value="debt" sx={{ minHeight: 52, fontSize: 16 }}>Lo debo</ToggleButton>
      </ToggleButtonGroup>
      {payable && (
        <>
          <SupplierPicker value={supplier} onChange={setSupplier} required />
          <TextField label="Vence" type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} InputLabelProps={{ shrink: true }}
            InputProps={{ sx: { fontSize: 18, minHeight: 56 } }} />
        </>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      <Button variant="contained" size="large" color="success" onClick={submit} disabled={saving} sx={{ minHeight: 60, fontSize: 20, fontWeight: 800 }}>
        {saving ? 'Guardando…' : `Guardar ${amountNum > 0 ? formatMoney(amountNum) : ''}`}
      </Button>
    </Stack>
  );
};

export default MobileExpensePage;
